/**
 * The object that represents the account's current configurations.
 *
 * @remarks
 * All property types must with either be `string` or `string | undefined`.
 * The `Partial&lt;Record&lt;keyof CmpAppConfigObject, string | undefined&gt;&gt;` ensures
 * that all properties follow this requirement.
 */
interface CmpBasAppConfigObject {
  appName?: string;
  appTitle?: string;
  timeZone?: string;
  locale?: string;
  currencyName?: string;
  smsTermsAndConditionsUrl?: string;
  companyName?: string;
  customerServicePhoneNumber?: string;
  customerServiceEmail?: string;
}

export type CmpAppConfigObject = Partial<Record<keyof CmpBasAppConfigObject, string>>

export class CmpMaintenanceModeObject {
  maintenanceMode = false;
  scheduled = false;
  message = '';
  title = '';
  scheduledTime = ''
}

export class CmpGoogleMapOptionObject {
  mapTypeControl = false;
  mapTypeId = 'roadmap'; // Default
  fullscreenControl = false;
  zoomControl = true;
  scrollwheel = false;
  disableDoubleClickZoom = true;
  minZoom: 8; // default value set on application
  maxZoom: 15; // default value set on application
}
export class CmpGoogleMapPropertyObject {
  zoom = 8; // default value set on application
}

type Nullable<T> = { [ P in keyof T]: T[P] | null };
export class CmpAppConfigObjectHelper {
  public static isCmpAppConfigObject(value: any): value is CmpAppConfigObject {
    return typeof value === 'object' && value !== null &&
      (typeof value.appName === 'undefined' || typeof value.appName === 'string') &&
      (typeof value.appTitle === 'undefined' || typeof value.appTitle === 'string') &&
      (typeof value.timeZone === 'undefined' || typeof value.timeZone === 'string') &&
      (typeof value.locale === 'undefined' || typeof value.locale === 'string') &&
      (typeof value.currencyName === 'undefined' || typeof value.currencyName === 'string') &&
      (typeof value.smsTermsAndConditionsUrl === 'undefined' || typeof value.smsTermsAndConditionsUrl === 'string') &&
      (typeof value.companyName === 'undefined' || typeof value.companyName === 'string') &&
      (typeof value.customerServicePhoneNumber === 'undefined' || typeof value.customerServicePhoneNumber === 'string') &&
      (typeof value.customerServiceEmail === 'undefined' || typeof value.customerServiceEmail === 'string');
  }

  public static isNullableCmpAppConfigObject(value: any): value is Nullable<CmpAppConfigObject> {
    return typeof value === 'object' && value !== null &&
      (typeof value.appName === 'undefined' || typeof value.appName === 'string' || value.appName === null) &&
      (typeof value.appTitle === 'undefined' || typeof value.appTitle === 'string' || value.appTitle === null) &&
      (typeof value.timeZone === 'undefined' || typeof value.timeZone === 'string' || value.timeZone === null) &&
      (typeof value.locale === 'undefined' || typeof value.locale === 'string' || value.locale === null) &&
      (typeof value.currencyName === 'undefined' || typeof value.currencyName === 'string' || value.currencyName === null) &&
      (typeof value.smsTermsAndConditionsUrl === 'undefined'
        || typeof value.smsTermsAndConditionsUrl === 'string' || value.smsTermsAndConditionsUrl === null) &&
        (typeof value.companyName === 'undefined' || typeof value.companyName === 'string' || value.companyName === null) &&
        (typeof value.customerServicePhoneNumber === 'undefined' || typeof value.customerServicePhoneNumber === 'string'
          || value.customerServicePhoneNumber === null) &&
          (typeof value.customerServiceEmail === 'undefined' || typeof value.customerServiceEmail === 'string'
            || value.customerServiceEmail === null);
  }

  /**
   *
   * @param baseObj - The base object to replace values in.
   * @param newObj - The new object with values to be used to replace.  If a
   * value is `null`, it will set the merged object's value to 'undefined'. Any
   * `undefined` values will use the `baseObj` value for that property.
   */
  public static merge(baseObj: CmpAppConfigObject, newObj: Nullable<CmpAppConfigObject>): CmpAppConfigObject {
    return {
      appName: newObj.appName === null ? undefined : (newObj.appName || baseObj.appName),
      appTitle: newObj.appTitle === null ? undefined : (newObj.appTitle || baseObj.appTitle),
      timeZone: newObj.timeZone === null ? undefined : (newObj.timeZone || baseObj.timeZone),
      locale: newObj.locale === null ? undefined : (newObj.locale || baseObj.locale),
      currencyName: newObj.currencyName === null ? undefined : (newObj.currencyName || baseObj.currencyName),
      smsTermsAndConditionsUrl: newObj.smsTermsAndConditionsUrl === null ? undefined
        : (newObj.smsTermsAndConditionsUrl || baseObj.smsTermsAndConditionsUrl),
      companyName: newObj.companyName === null ? undefined
      : (newObj.companyName || baseObj.companyName),
      customerServicePhoneNumber: newObj.customerServicePhoneNumber === null ? undefined
      : (newObj.customerServicePhoneNumber || baseObj.customerServicePhoneNumber),
      customerServiceEmail: newObj.customerServiceEmail === null ? undefined
      : (newObj.customerServiceEmail || baseObj.customerServiceEmail),
    };
  }
}